import React, { useState, useEffect } from 'react';
import {
  Table,
  Input,
  Space,
  Card,
  Typography,
  Tag,
  message,
  Modal,
  Descriptions,
  Popover
} from 'antd';
import { SearchOutlined, UserOutlined } from '@ant-design/icons';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { API_BASE_URL, getAuthHeaders } from '../api';
import type { Athlete } from '../types';

const { Title } = Typography;
const { Search } = Input;

const Athletes: React.FC = () => {
  const { user } = useAuth();
  const [athletes, setAthletes] = useState<Athlete[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [hasSearched, setHasSearched] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [selectedAthlete, setSelectedAthlete] = useState<Athlete | null>(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  useEffect(() => {
    if (!user) return;
    if (!hasSearched) return;
    fetchAthletes();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- pagination.current/pageSize are intentional triggers
  }, [user, pagination.current, pagination.pageSize, searchText, hasSearched]);

  const fetchAthletes = async () => {
    setLoading(true);
    try {
      const headers = await getAuthHeaders();
      const response = await axios.get(`${API_BASE_URL}/api/athletes`, {
        params: {
          page: pagination.current,
          per_page: pagination.pageSize,
          search: searchText || undefined,
        },
        headers,
      });

      const data = response.data;
      setAthletes(data.athletes ?? []);
      setPagination(prev => ({
        ...prev,
        total: data.pagination?.total ?? data.total ?? 0,
      }));
    } catch (error) {
      message.error('Failed to fetch athletes');
      console.error('Error fetching athletes:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleTableChange = (newPagination: any) => {
    setPagination(prev => ({
      ...prev,
      current: newPagination.current,
      pageSize: newPagination.pageSize,
    }));
  };

  const handleSearch = (value: string) => {
    const trimmedValue = value.trim();
    const shouldSearch = trimmedValue.length > 0;
    setHasSearched(shouldSearch);
    setSearchText(trimmedValue);
    setPagination(prev => ({
      ...prev,
      current: 1,
      total: shouldSearch ? prev.total : 0,
    }));
    if (!shouldSearch) setAthletes([]);
  };

  const showAthleteDetails = (athlete: Athlete) => {
    setSelectedAthlete(athlete);
    setModalVisible(true);
  };

  const calculateAge = (birthDate: string) => {
    const today = new Date();
    const birth = new Date(birthDate);
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    return age;
  };

  const renderNoWrapHeader = (title: string) => (
    <span style={{ whiteSpace: 'nowrap', display: 'inline-block' }}>{title}</span>
  );

  const renderDisciplines = (disciplines?: string[] | null) => {
    const safeDisciplines = Array.isArray(disciplines)
      ? disciplines.filter(Boolean)
      : [];

    if (!safeDisciplines.length) {
      return <Tag color="default">No disciplines</Tag>;
    }

    const visible = safeDisciplines.slice(0, 3);
    const hiddenCount = safeDisciplines.length - visible.length;

    const chips = visible.map((discipline, index) => (
      <Tag
        key={index}
        color="green"
        style={{
          whiteSpace: 'normal',
          wordBreak: 'break-word',
          margin: 0,
        }}
      >
        {discipline}
      </Tag>
    ));

    if (hiddenCount > 0) {
      chips.push(
        <Popover
          key="more"
          overlayStyle={{ maxWidth: 320 }}
          content={
            <Space direction="vertical" size={4} style={{ display: 'block' }}>
              {safeDisciplines.slice(3).map((discipline, idx) => (
                <Tag key={idx} color="green" style={{ margin: 0, whiteSpace: 'normal', wordBreak: 'break-word' }}>
                  {discipline}
                </Tag>
              ))}
            </Space>
          }
        >
          <Tag color="default" style={{ margin: 0 }}>
            +{hiddenCount}
          </Tag>
        </Popover>
      );
    }

    return (
      <Space size={[6, 6]} wrap style={{ maxWidth: 260 }}>
        {chips}
      </Space>
    );
  };

  const columns = [
    {
      title: 'Name',
      key: 'name',
      ellipsis: true,
      width: 280,
      render: (record: Athlete) => (
        <Space style={{ wordWrap: 'break-word', overflowWrap: 'break-word' }}>
          <UserOutlined />
          <Typography.Text strong style={{ wordBreak: 'break-word', overflowWrap: 'break-word' }}>
            {record.full_name}
          </Typography.Text>
        </Space>
      ),
    },
    {
      title: renderNoWrapHeader('Gender'),
      dataIndex: 'gender',
      key: 'gender',
      width: 110,
      render: (gender: string) => {
        const isMale = gender && (gender.toLowerCase() === 'male' || gender === 'M');
        return (
          <Tag color={isMale ? 'blue' : 'pink'}>
            {isMale ? 'Male' : 'Female'}
          </Tag>
        );
      },
    },
    {
      title: 'Age',
      key: 'age',
      width: 80,
      render: (record: Athlete) => {
        if (!record.birth_date) return 'N/A';
        return calculateAge(record.birth_date);
      },
    },
    {
      title: renderNoWrapHeader('Birth Date'),
      dataIndex: 'birth_date',
      key: 'birth_date',
      width: 140,
      align: 'center' as const,
      render: (date: string) => date ? new Date(date).toLocaleDateString() : 'N/A',
    },
    {
      title: renderNoWrapHeader('Country'),
      dataIndex: 'country_name',
      key: 'country_name',
      width: 150,
      render: (text: string) => (
        <Tag color="blue" style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
          {text}
        </Tag>
      ),
    },
    {
      title: renderNoWrapHeader('Disciplines'),
      dataIndex: 'disciplines',
      key: 'disciplines',
      width: 280,
      render: renderDisciplines,
    },
  ];

  return (
    <div className="page-container">
      <Title level={2} style={{ fontSize: isMobile ? '20px' : undefined }}>Athletes</Title>

      <Card style={{ marginBottom: 16 }} styles={{ body: isMobile ? { padding: 12 } : {} }}>
        <Space direction="vertical" style={{ width: '100%' }}>
          <Search
            placeholder="Search athletes by name or country..."
            allowClear
            enterButton={<SearchOutlined />}
            size={isMobile ? 'middle' : 'large'}
            onSearch={handleSearch}
            style={{ maxWidth: isMobile ? '100%' : 400 }}
          />
        </Space>
      </Card>

      <Card styles={{ body: { padding: 0, overflow: 'hidden' } }}>
        <Table
          columns={columns}
          dataSource={athletes}
          rowKey="id"
          loading={loading}
          onRow={(record) => ({
            onClick: () => showAthleteDetails(record),
            style: { cursor: 'pointer' },
          })}
          locale={{
            emptyText: searchText
              ? 'No athletes found'
              : 'Search for an Athlete',
          }}
          size={isMobile ? 'small' : 'middle'}
          pagination={{
            ...pagination,
            showSizeChanger: false,
            showQuickJumper: false,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} athletes`,
            size: isMobile ? 'small' : undefined,
          }}
          onChange={handleTableChange}
          scroll={{ x: isMobile ? 800 : 1100 }}
          tableLayout="auto"
        />
      </Card>

      <Modal
        title="Athlete Details"
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        width={isMobile ? '95%' : 600}
        style={isMobile ? { maxWidth: 400 } : undefined}
      >
        {selectedAthlete && (
          <Descriptions bordered column={1}>
            <Descriptions.Item label="Full Name" span={2}>
              {selectedAthlete.full_name}
            </Descriptions.Item>
            <Descriptions.Item label="Given Name">
              {selectedAthlete.given_name}
            </Descriptions.Item>
            <Descriptions.Item label="Family Name">
              {selectedAthlete.family_name}
            </Descriptions.Item>
            <Descriptions.Item label="Gender">
              <Tag color={selectedAthlete.gender && (selectedAthlete.gender.toLowerCase() === 'male' || selectedAthlete.gender === 'M') ? 'blue' : 'pink'}>
                {selectedAthlete.gender && (selectedAthlete.gender.toLowerCase() === 'male' || selectedAthlete.gender === 'M') ? 'Male' : 'Female'}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Birth Date">
              {selectedAthlete.birth_date ? new Date(selectedAthlete.birth_date).toLocaleDateString() : 'N/A'}
            </Descriptions.Item>
            <Descriptions.Item label="Age">
              {selectedAthlete.birth_date ? calculateAge(selectedAthlete.birth_date) : 'N/A'}
            </Descriptions.Item>
            <Descriptions.Item label="Country">
              <Tag color="blue">{selectedAthlete.country_name}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Disciplines">
              {selectedAthlete.disciplines?.map((discipline, index) => (
                <Tag key={index} color="green" style={{ marginBottom: 4 }}>
                  {discipline}
                </Tag>
              ))}
            </Descriptions.Item>
          </Descriptions>
        )}
      </Modal>
    </div>
  );
};

export default Athletes;
